import cn from 'classnames';
import React from 'react';
import { Container } from '../Container';
import { ReactComponent as Underline } from './underline.svg';

type Tab = {
  component: React.ReactNode;
  label: React.ReactNode;
  tabName: string;
};

interface IProps {
  tabs: Tab[];
  activeTabName?: string;
  onTabClick: (tabName: string) => void;
  id?: string;
}

/**
 * Tabs is a controlled component that accepts a list of tabs, a selected tab name to display content, and a click handler
 *
 * @param tabs - List of tabs to display, including display name, tab name, and component to render
 * @param activeTabName - The current selected tab
 * @param onTabClick - function to call when a tab is clicked. Includes `tabName` as an argument
 * @returns Tab component and the current selected tab's component
 */
export const Tabs: React.FC<IProps> = ({ tabs, onTabClick, activeTabName }) => {
  const activeTabIndex = tabs.findIndex(({ tabName }) => tabName === activeTabName);
  const buttonWidthAsPct = 100 / tabs.length;

  return (
    <div>
      <div className='sticky top-[47px] z-tabs border-b border-b-eggplant-100 bg-white'>
        <Container noPadding className='relative'>
          <div className='flex justify-center'>
            {tabs.map(({ label, tabName }) => {
              const isActiveTab = activeTabName === tabName;
              return (
                <div className='w-full' key={tabName}>
                  <button
                    className={cn('w-full pb-1.5 pt-2 text-center text-caption-700 uppercase outline-none', {
                      'text-eggplant-400': !isActiveTab,
                      'text-raspberry': isActiveTab,
                    })}
                    onClick={() => onTabClick(tabName)}>
                    {label}
                  </button>
                </div>
              );
            })}
            <div className='absolute -bottom-0.5 w-full'>
              <Underline
                style={{
                  width: `${buttonWidthAsPct}%`,
                  transform: `translateX(${100 * activeTabIndex}%)`,
                }}
                className='h-[5px] w-full transition-transform duration-150'
              />
            </div>
          </div>
        </Container>
      </div>
      <div>{tabs[activeTabIndex]?.component}</div>
    </div>
  );
};
